import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { useState } from 'react'
import { Helmet } from 'react-helmet'

import { useActiveStoreView } from '@emico/storeviews'
import { useBreakpoints } from '@emico/ui'

import ResultDetailOverlay from './ResultDetailOverlay'
import SearchForm from './SearchForm'
import StoreMap from './StoreMap'
import Stores from './Stores'
import { SrsStore } from './useSrsStore'
import { LocationProps, StoreFilter, useSrsStores } from './useSrsStores'
import { Responsive } from '../core/Responsive'
import { SrsStoreFilterInput } from '../graphql/schema.generated'
import Header from '../layout/Header/Header'
import HeaderContainer from '../layout/Header/HeaderContainer'
import PageWrapper from '../layout/PageWrapper'
import PageLoader from '../presentation/PageLoader'
import PrismicCmsSlices from '../PrismicCmsSlices'
import PrismicGrid from '../PrismicGrid'
import usePrismicStoreLocatorPage from '../usePrismicStoreLocatorPage'

const StyledContainer = styled.div`
    height: 80vh;
    width: 100%;
`

const StoreLocatorPage = () => {
    const [selectedStore, setSelectedStore] = useState<SrsStore>()
    const [selectedStoreLocation, setSelectedStoreLocation] =
        useState<LocationProps>()
    const [currentLocation, setCurrentLocation] = useState<LocationProps>()
    const { isMobile } = useBreakpoints()

    const activeStore = useActiveStoreView()
    let filters: SrsStoreFilterInput = {}
    let storeView: StoreFilter = ''

    // Limit results to types of stores relevant to the site being viewed.
    // CI only lists stores that sell CI, and does not show PME branded stores.
    // PME only lists stores that sell PME, and does not show CI branded stores.
    // VG only lists stores that sell VG.
    // No need to exclude vg branded stores because there aren't any.
    if (activeStore.code.includes('castiron')) {
        storeView = 'castiron'
        filters = {
            ci: true,
            pme_store: false,
        }
    } else if (activeStore.code.includes('pme')) {
        storeView = 'pme'
        filters = {
            pme: true,
            ci_store: false,
        }
    } else if (activeStore.code.includes('vanguard')) {
        storeView = 'vanguard'
        filters = {
            vg: true,
            ci_store: false,
            pme_store: false,
        }
    } else if (activeStore.code.includes('justbrands')) {
        storeView = 'justbrands'
        filters = {
            jb_store: true,
        }
    }

    const {
        data: stores,
        filteredStores,
        loading,
    } = useSrsStores({
        coordinates: selectedStoreLocation,
        filters,
        pageSize: 3000,
        skipStock: true,
    })

    const { data: storelocatorData } = usePrismicStoreLocatorPage()
    const showStores = Boolean(storelocatorData?.showStores)
    const showJbStores = Boolean(storelocatorData?.showJbStores)
    const brandHeading =
        storelocatorData?.brandListHeading ||
        t({
            id: 'storelocator.page.storeTitle',
            message: `Brand stores`,
        })

    const brandSubheading =
        storelocatorData?.brandListSubheading ||
        t({
            id: 'storelocator.page.storeSubTitle',
            message: `Visit your nearest store`,
        })
    const jbHeading = storelocatorData?.jbListHeading || undefined
    const jbSubheading = storelocatorData?.jbListSubheading || undefined

    return (
        <PageWrapper
            pageType="cms"
            header={
                <HeaderContainer>
                    <Header />
                </HeaderContainer>
            }
        >
            <Helmet>
                <title>
                    {t({
                        id: 'storelocator.page.pageTitle',
                        message: `PME Legend stores | Find a store near you`,
                    })}
                </title>
                <meta
                    name="description"
                    content={t({
                        id: 'storelocator.page.pageDescription',
                        message: `Find a PME Legend store near you. View the opening hours of our PME Legend stores and come by to shop.`,
                    })}
                />
            </Helmet>

            {loading && <PageLoader fullScreen />}
            <Responsive sm up>
                <StyledContainer>
                    {!loading && (
                        <StoreMap
                            stores={
                                filteredStores?.length ? filteredStores : stores
                            }
                            amountResults={
                                filteredStores?.length
                                    ? filteredStores.length
                                    : 0
                            }
                            location={currentLocation}
                            selectedStoreLocation={selectedStoreLocation}
                            selectedStore={selectedStore}
                            onSelect={setSelectedStore}
                            storeView={storeView}
                            zoomLevel={{
                                noResults: activeStore.code
                                    .toLowerCase()
                                    .includes('_de')
                                    ? 7
                                    : 8,
                            }}
                        />
                    )}
                </StyledContainer>
            </Responsive>

            <SearchForm
                searchResults={filteredStores}
                storeView={storeView}
                onSelect={setSelectedStore}
                selectedStore={selectedStore}
                selectedStoreLocation={selectedStoreLocation}
                setSelectedStoreLocation={(
                    location: LocationProps | undefined,
                ) => setSelectedStoreLocation(location)}
                setCurrentLocation={setCurrentLocation}
                onClose={() => setSelectedStore(undefined)}
                resetForm={() => {
                    setSelectedStore(undefined)
                    setSelectedStoreLocation(undefined)
                }}
                searchResultsLoading={loading}
            />

            <Responsive sm down>
                <ResultDetailOverlay
                    selectedStore={selectedStore}
                    storeView={storeView}
                    onClose={() => setSelectedStore(undefined)}
                    css={css`
                        ${isMobile
                            ? selectedStore
                                ? 'opacity: 0.3'
                                : 'opacity: 1'
                            : ''}
                    `}
                />
            </Responsive>

            {showStores && (
                <div
                    // eslint-disable-next-line react/no-unknown-property
                    css={css`
                        margin-bottom: 50px;
                        ${isMobile
                            ? selectedStore
                                ? 'opacity: 0.3'
                                : 'opacity: 1'
                            : ''}
                    `}
                >
                    <Stores
                        title={brandHeading}
                        description={brandSubheading}
                        storeView={storeView}
                    />
                </div>
            )}

            {showJbStores && jbHeading && (
                <div
                    // eslint-disable-next-line react/no-unknown-property
                    css={css`
                        margin-bottom: 50px;
                        ${isMobile
                            ? selectedStore
                                ? 'opacity: 0.3'
                                : 'opacity: 1'
                            : ''}
                    `}
                >
                    <Stores
                        title={jbHeading}
                        description={jbSubheading}
                        storeView="justbrands"
                    />
                </div>
            )}

            {storelocatorData?.body && (
                <PrismicGrid>
                    <PrismicCmsSlices
                        slices={storelocatorData.body}
                        prismicType="StoreLocatorPage"
                    />
                </PrismicGrid>
            )}
        </PageWrapper>
    )
}

export default StoreLocatorPage
